import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

// Vision UI layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import { api } from "../../api";
import { 
  IoLogoGithub, 
  IoCheckmarkCircle, 
  IoKey, 
  IoRocketSharp, 
  IoChevronDown,
  IoSearchOutline, 
  IoShieldCheckmarkOutline,
  IoGitBranchOutline
} from "react-icons/io5";

const darkInputSx = {
  backgroundColor: "#0b1437 !important",
  borderRadius: "8px",
  border: "1px solid rgba(255, 255, 255, 0.25) !important",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0b1437 !important",
    color: "#ffffff !important",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "transparent !important",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4) !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0075ff !important",
    },
  },
  "& .MuiInputBase-root": {
    backgroundColor: "#0b1437 !important",
    color: "#ffffff !important",
  },
  "& .MuiInputBase-input": {
    color: "#ffffff !important",
    backgroundColor: "transparent !important",
    fontSize: "0.875rem",
    "&::placeholder": {
      color: "rgba(255, 255, 255, 0.5) !important",
      opacity: "1 !important",
    },
  },
};

export default function GitHubPage() {
  const history = useHistory();
  const location = useLocation();

  const [status, setStatus] = useState({ connected: false, username: null });
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthUrl, setOauthUrl] = useState(null);
  const [tokenInput, setTokenInput] = useState("");
  const [connectingToken, setConnectingToken] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [repoSearch, setRepoSearch] = useState("");

  const loadGitHub = async () => {
    try {
      const sRes = await api.getGitHubStatus();
      if (sRes.success && sRes.data) {
        setStatus(sRes.data);
        if (sRes.data.connected) {
          const rRes = await api.getGitHubRepos();
          if (rRes.success && rRes.data) {
            setRepos(rRes.data.repos || []);
          }
        }
      }

      const authRes = await api.getGitHubAuthUrl();
      if (authRes.success && authRes.data?.configured && authRes.data?.url) {
        setOauthUrl(authRes.data.url);
      }
    } catch (err) {
      console.error("Failed to load GitHub info:", err);
    } finally {
      setLoading(false);
    }
  };

  // Check for OAuth callback ?code=...
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = params.get("state");

    if (code) {
      setOauthLoading(true);
      setError("");
      api.githubCallback(code, state)
        .then((res) => {
          if (res.success) {
            setSuccess(`Successfully connected GitHub as @${res.data?.username || "user"}!`);
            history.replace("/github");
            loadGitHub();
          } else {
            setError(res.error?.message || "Failed to exchange GitHub authorization code.");
          }
        })
        .catch((err) => {
          setError(err.message || "Failed to complete GitHub OAuth.");
        })
        .finally(() => {
          setOauthLoading(false);
        });
    } else {
      loadGitHub();
    }
  }, [location.search]);

  const handleConnectToken = async (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    setError("");
    setSuccess("");
    setConnectingToken(true);

    try {
      const res = await api.connectGitHubToken(tokenInput.trim());
      if (res.success && res.data?.connected) {
        setSuccess(`Connected as @${res.data.username}! Repositories loaded.`);
        setTokenInput("");
        await loadGitHub();
      }
    } catch (err) {
      setError(err.message || "Failed to connect GitHub token. Please verify permissions.");
    } finally {
      setConnectingToken(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect your GitHub account? Active sites will continue serving, but automated builds from private repositories will pause.")) {
      return;
    }
    try {
      await api.disconnectGitHub();
      setStatus({ connected: false, username: null });
      setRepos([]);
      setSuccess("GitHub account disconnected successfully.");
      await loadGitHub();
    } catch (err) {
      alert("Failed to disconnect: " + err.message);
    }
  };

  const filteredRepos = repos.filter((r) => 
    r.fullName.toLowerCase().includes(repoSearch.toLowerCase()) ||
    (r.description && r.description.toLowerCase().includes(repoSearch.toLowerCase()))
  );

  const repoColumns = [
    { name: "repository", align: "left" },
    { name: "visibility", align: "center" },
    { name: "default branch", align: "center" },
    { name: "updated", align: "center" },
    { name: "action", align: "right" },
  ];

  const repoRows = filteredRepos.map((r) => ({
    repository: (
      <VuiBox display="flex" flexDirection="column">
        <VuiTypography variant="button" color="white" fontWeight="bold">
          {r.fullName}
        </VuiTypography>
        <VuiTypography variant="caption" color="text">
          {r.description || "No description provided"}
        </VuiTypography>
      </VuiBox>
    ),
    visibility: (
      <VuiBadge
        color={r.private ? "warning" : "info"}
        variant="standard"
        size="sm"
        badgeContent={r.private ? "PRIVATE" : "PUBLIC"}
      />
    ),
    "default branch": (
      <VuiBadge
        color="dark"
        variant="gradient"
        size="sm"
        badgeContent={r.defaultBranch}
      />
    ),
    updated: (
      <VuiTypography variant="caption" color="text">
        {new Date(r.updatedAt).toLocaleDateString()}
      </VuiTypography>
    ),
    action: (
      <VuiButton
        variant="gradient"
        color="info"
        size="small"
        onClick={() => history.push(`/sites?repo=${encodeURIComponent(r.htmlUrl)}&name=${encodeURIComponent(r.name)}`)}
      >
        <IoRocketSharp style={{ marginRight: "4px" }} /> Deploy Site
      </VuiButton>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h4" color="white" fontWeight="bold">
              GitHub Integration
            </VuiTypography>
            <VuiTypography variant="button" color="text" fontWeight="regular">
              Connect your GitHub account for continuous 1-click deployments and private repository hosting
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {oauthLoading && (
          <Card sx={{ mb: 3, p: 4, textAlign: "center" }}>
            <CircularProgress color="info" size={42} sx={{ mb: 2 }} />
            <VuiTypography variant="h5" color="white" fontWeight="bold" mb={1}>
              Connecting to GitHub...
            </VuiTypography>
            <VuiTypography variant="button" color="text">
              Exchanging authorization code and configuring repository permissions. Please wait...
            </VuiTypography>
          </Card>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: "rgba(244,67,54,0.15)", color: "#ff8a80", border: "1px solid rgba(244,67,54,0.3)" }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3, backgroundColor: "rgba(0,230,118,0.15)", color: "#b9f6ca", border: "1px solid rgba(0,230,118,0.3)" }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Main GitHub Connection Card */}
          <Grid item xs={12}>
            <Card>
              <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                <VuiBox display="flex" alignItems="center" gap={2}>
                  <VuiBox
                    width="54px"
                    height="54px"
                    borderRadius="16px"
                    bgColor="dark"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ border: "1px solid rgba(255, 255, 255, 0.12)" }}
                  >
                    <IoLogoGithub size="32px" color="#fff" />
                  </VuiBox>
                  <VuiBox>
                    <VuiTypography variant="h5" color="white" fontWeight="bold">
                      GitHub Connection
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      {status.connected ? `Connected as @${status.username}` : "Not connected"}
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
                <VuiBadge
                  color={status.connected ? "success" : "warning"}
                  variant="standard"
                  size="md"
                  badgeContent={status.connected ? "CONNECTED" : "NOT CONNECTED"}
                />
              </VuiBox>

              {status.connected ? (
                <VuiBox pt={1}>
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} p={2} sx={{ backgroundColor: "rgba(0, 117, 255, 0.08)", borderRadius: "10px", border: "1px solid rgba(0, 117, 255, 0.2)" }}>
                    <VuiBox display="flex" alignItems="center" gap={1.5}>
                      <IoCheckmarkCircle size="24px" color="#00e676" />
                      <VuiBox>
                        <VuiTypography variant="button" color="white" fontWeight="bold" display="block">
                          GitHub Account Active (@{status.username})
                        </VuiTypography>
                        <VuiTypography variant="caption" color="text">
                          You can now deploy any public or private repository directly to Intent Web Hosting with automatic SSL.
                        </VuiTypography>
                      </VuiBox>
                    </VuiBox>
                    <VuiButton variant="outlined" color="error" size="small" onClick={handleDisconnect}>
                      Disconnect GitHub
                    </VuiButton>
                  </VuiBox>
                </VuiBox>
              ) : (
                <VuiBox pt={2}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <VuiTypography variant="body2" color="text" mb={2}>
                        Connect your GitHub account in one click to deploy your static repositories, receive automated Let's Encrypt SSL, and host private projects with zero hassle.
                      </VuiTypography>

                      <VuiBox mb={3} display="flex" flexDirection="column" gap={1}>
                        <VuiTypography variant="caption" color="text" display="flex" alignItems="center" gap={1}>
                          <IoShieldCheckmarkOutline color="#0075ff" size="16px" />
                          <strong>1-Click OAuth Authorization:</strong> No Personal Access Tokens or API keys required.
                        </VuiTypography>
                        <VuiTypography variant="caption" color="text" display="flex" alignItems="center" gap={1}>
                          <IoGitBranchOutline color="#0075ff" size="16px" />
                          <strong>Public & Private Support:</strong> Seamlessly deploy any repository you have access to.
                        </VuiTypography>
                      </VuiBox>

                      {oauthUrl ? (
                        <VuiButton
                          variant="gradient"
                          color="info"
                          size="large"
                          component="a"
                          href={oauthUrl}
                          sx={{ px: 4, py: 1.5, fontSize: "0.95rem" }}
                        >
                          <IoLogoGithub size="22px" style={{ marginRight: "10px" }} />
                          Connect with GitHub (1-Click OAuth)
                        </VuiButton>
                      ) : (
                        <VuiBox display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} color="info" />
                          <VuiTypography variant="button" color="text">
                            Loading GitHub OAuth configuration...
                          </VuiTypography>
                        </VuiBox>
                      )}
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Accordion sx={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px !important" }}>
                        <AccordionSummary expandIcon={<IoChevronDown color="#fff" />}>
                          <VuiTypography variant="button" color="text" display="flex" alignItems="center" gap={1}>
                            <IoKey size="16px" /> Alternative: Connect with Token
                          </VuiTypography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <form onSubmit={handleConnectToken}>
                            <VuiTypography variant="caption" color="text" display="block" mb={1.5}>
                              Enter a GitHub Personal Access Token (classic or fine-grained) with <code>repo</code> permissions:
                            </VuiTypography>
                            <VuiBox mb={1.5}>
                              <TextField
                                fullWidth
                                type="password"
                                placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                value={tokenInput}
                                onChange={(e) => setTokenInput(e.target.value)}
                                size="small"
                                sx={darkInputSx}
                              />
                            </VuiBox>
                            <VuiButton type="submit" variant="outlined" color="white" size="small" disabled={connectingToken}>
                              {connectingToken ? "Verifying..." : "Save Token"}
                            </VuiButton>
                          </form>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Grid>
                </VuiBox>
              )}
            </Card>
          </Grid>

          {/* Repositories Explorer when connected */}
          {status.connected && (
            <Grid item xs={12}>
              <Card>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
                  <VuiBox>
                    <VuiTypography variant="h5" color="white" fontWeight="bold">
                      Your GitHub Repositories ({repos.length})
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text">
                      Choose any repository to build and deploy to a free <code>*.web.int.yt</code> subdomain
                    </VuiTypography>
                  </VuiBox>

                  <VuiBox width="280px">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search repositories..."
                      value={repoSearch}
                      onChange={(e) => setRepoSearch(e.target.value)}
                      sx={darkInputSx}
                      InputProps={{
                        startAdornment: <IoSearchOutline size="16px" color="rgba(255,255,255,0.6)" style={{ marginRight: "8px" }} />
                      }}
                    />
                  </VuiBox>
                </VuiBox>

                {loading ? (
                  <VuiBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress color="info" />
                  </VuiBox>
                ) : filteredRepos.length === 0 ? (
                  <VuiBox py={6} textAlign="center">
                    <VuiTypography variant="button" color="text">
                      {repoSearch ? "No repositories match your search." : "No repositories found for your GitHub account."}
                    </VuiTypography>
                  </VuiBox>
                ) : (
                  <Table columns={repoColumns} rows={repoRows} />
                )}
              </Card>
            </Grid>
          )}
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}
